import { apolloClient } from "@/frontend/apollo";
import {
  BUILD_TARGET,
  USER_PROFILE_LOCALSOTORAGE_KEY,
} from "@/frontend/globals";
import {
  clearLocal,
  getLocal,
  getQueryVariablesFromURL,
  getUrlQueryFromDictionary,
  isAdultDisabled,
  setLocal,
} from "@/frontend/lib/utils";
import { RootState } from "frontend/store/index";
import gql from "graphql-tag";
import { ActionTree, GetterTree, MutationTree } from "vuex";

interface ICategory {
  id: string;
  isAdult: boolean;
  name: string;
}

interface ISubCategory extends ICategory {
  parentCategory: string;
}

interface ITopCategory extends ICategory {
  childs: ICategory[];
}

export class State {
  public categories: ICategory[] = [];
  public indexedCategories: Map<string, ICategory> = new Map();
  public loadingCategories: false;
  public subCategories: ISubCategory[] = [];
  public topCategories: ITopCategory[] = [];
}

const getters: GetterTree<State, any> = {
  preferredBookCategories(
    state: State,
    gets,
    rootState: RootState,
    rootGetters,
  ) {
    const user = rootState.user;
    const isLoggedin = rootGetters.isLoggedin;

    let prefCats: any[] = [];

    if (isLoggedin) {
      prefCats = user.preferredBookCategories;
    } else {
      const localUserPrefs = rootState.localUserProfile;
      prefCats = localUserPrefs && localUserPrefs.preferredBookCategories;
    }

    if (!prefCats) {
      prefCats = [];
    }

    prefCats = prefCats.filter((cat) => cat.parentCategory !== null);

    if (isAdultDisabled()) {
      prefCats = prefCats.filter((cat) => !cat.isAdult);
    }
    return prefCats;
  },
  preferredCategoriesIndexes(state: State, gets, rootState: RootState) {
    return gets.preferredBookCategories.map((catObj) => catObj.id);
  },
  userIsAdult(state: State, gets, rootState: RootState, rootGetters) {
    const isLoggedin = rootGetters.isLoggedin;

    const userCats = isLoggedin
      ? rootState.user && rootState.user.preferredBookCategories
      : rootState.localUserProfile &&
        rootState.localUserProfile.preferredBookCategories;

    if (!userCats) {
      return false;
    }

    return Boolean(userCats.find((cat) => cat && cat.isAdult));
  },
  localOnboardingComplete(
    state: State,
    gets,
    rootState: RootState,
    rootGetters,
  ) {
    return (
      rootState.localUserProfile &&
      rootState.localUserProfile.bookInfo &&
      rootState.localUserProfile.bookInfo.bookOnboardingComplete
    );
  },
  onboardingComplete(state: State, gets, rootState: RootState, rootGetters) {
    const isLoggedin = rootGetters.isLoggedin;
    if (isLoggedin) {
      return (
        rootState.user &&
        rootState.user.bookInfo &&
        rootState.user.bookInfo.bookOnboardingComplete
      );
    } else {
      return gets.localOnboardingComplete;
    }
  },
};

const mutations: MutationTree<State> = {
  categories(state, categories) {
    state.categories = categories;

    if (!state.categories) {
      return;
    }

    const inxCat = new Map<string, ICategory>();
    state.categories.forEach((cat) => {
      inxCat.set(cat.id, cat);
    });
    state.indexedCategories = inxCat;

    let topCategories = categories.filter(
      (cat) => cat.parentCategory === null,
    );
    const subCategories = categories.filter(
      (cat) => cat.parentCategory !== null,
    );
    subCategories.forEach((cat) => {
      const parentCat: ITopCategory = inxCat.get(
        cat.parentCategory,
      ) as ITopCategory;
      if (!parentCat) {
        return;
      }
      if (!parentCat.childs) {
        parentCat.childs = [];
      }
      parentCat.childs.push(cat);
    });

    topCategories = topCategories.filter((cat) => {
      if (!cat.childs || cat.childs.length === 0) {
        console.error(
          `Top Category ${cat.name} id:${cat.id} doesn't have subcategories`,
          cat,
        );
        return false;
      }
      return true;
    });

    state.subCategories = subCategories;
    state.topCategories = topCategories;
  },
  loadingCategories(state, loadingCategories) {
    state.loadingCategories = loadingCategories;
  },
};

const actions: ActionTree<State, any> = {
  setPreferredBookCategories(context, cats) {
    const user = {
      ...context.rootState.user,
      preferredBookCategories: cats,
    };
    context.commit("user", user, { root: true });
  },
  async fetchCategories(context) {
    if (context.state.loadingCategories) {
      return;
    }
    context.commit("loadingCategories", true);
    const res = await apolloClient.query({
      query: gql`
        query BookCategories {
          bookCategories(pagination: { size: 1000, page: 1 }) {
            entries {
              id
              name
              parentCategory
              isAdult
              icon(maxWidth: 140, maxHeight: 140) {
                url
              }
            }
            count
          }
        }
      `,
    });

    const data: any = res.data;
    let categories = data.bookCategories.entries;

    if (isAdultDisabled()) {
      categories = categories.filter((cat) => !cat.isAdult);
    }

    context.commit("categories", categories);
    context.commit("loadingCategories", false);
  },
  async savePrefCatsFromTopCats(context, topCats) {
    const subCats = topCats
      .map((topCatId) => {
        const topCat: ITopCategory = context.state.indexedCategories.get(
          topCatId,
        ) as ITopCategory;
        if (topCat && topCat.childs) {
          return topCat.childs.map((cat) => cat.id);
        }
      })
      .flat();

    await context.dispatch("savePreferredBookCategories", subCats);
  },
  async savePreferredBookCategories(context, catsIDs: [string]) {
    const isLoggedin = context.rootGetters.isLoggedin;
    if (isLoggedin) {
      // Save on server
      const ret = await apolloClient.mutate({
        mutation: gql`
          mutation SetPreferredBookCategories($categories: [ID]) {
            setPreferredBookCategories(preferredBookCategories: $categories) {
              preferredBookCategories {
                id
                name
                parentCategory
                isAdult
              }
            }
          }
        `,
        variables: {
          categories: catsIDs,
        },
      });
      const retCategories =
        ret.data.setPreferredBookCategories.preferredBookCategories;
      await context.dispatch("setPreferredBookCategories", retCategories);
    } else {
      // Save on client
      const cats = catsIDs.map((catID) =>
        context.state.indexedCategories.get(catID),
      );
      const savedUserProfile = getLocal(USER_PROFILE_LOCALSOTORAGE_KEY);
      const localUserProfile = {
        ...savedUserProfile,
        preferredBookCategories: cats,
      };
      setLocal(USER_PROFILE_LOCALSOTORAGE_KEY, localUserProfile);
      context.commit("localUserProfile", localUserProfile, { root: true });
    }
  },
  async saveOnboardingComplete(context, complete) {
    const isLoggedin = context.rootGetters.isLoggedin;
    if (isLoggedin) {
      const ret = await apolloClient.mutate({
        mutation: gql`
          mutation SetOnboardingComplete($complete: Boolean!) {
            setOnboardingComplete(bookOnboardingComplete: $complete) {
              bookOnboardingComplete
            }
          }
        `,
        variables: {
          complete,
        },
      });

      const user = {
        ...context.rootState.user,
        bookInfo: {
          bookOnboardingComplete:
            ret.data.setOnboardingComplete.bookOnboardingComplete,
        },
      };
      context.commit("user", user, { root: true });
    } else {
      const savedUserProfile = getLocal(USER_PROFILE_LOCALSOTORAGE_KEY);
      const localUserProfile = {
        ...savedUserProfile,
        bookInfo: {
          bookOnboardingComplete: complete,
        },
      };
      setLocal(USER_PROFILE_LOCALSOTORAGE_KEY, localUserProfile);
      context.commit("localUserProfile", localUserProfile, { root: true });
    }
  },
  async syncUserPreferences(context) {
    const isLoggedin = context.rootGetters.isLoggedin;
    const onboardingComplete = context.getters.onboardingComplete;
    const localOnboardingComplete = context.getters.localOnboardingComplete;

    console.log("syncUserPreferences ", {
      isLoggedin,
      onboardingComplete,
      localOnboardingComplete,
    });

    if (!isLoggedin) {
      return;
    }

    if (onboardingComplete) {
      return;
    }

    if (!localOnboardingComplete) {
      return;
    }

    console.log("-------> UPLOAD LOCAL USER PREFERENCE <-------");

    const cats = context.rootState.localUserProfile.preferredBookCategories;
    await context.dispatch(
      "savePreferredBookCategories",
      cats.map((cat) => cat.id),
    );
    await context.dispatch("saveOnboardingComplete", true);

    clearLocal(USER_PROFILE_LOCALSOTORAGE_KEY);
    await context.dispatch("fetchLocalUserProfile", {}, { root: true });
  },
  async viewBook(context, id) {
    let url;
    try {
      const res = await apolloClient.query({
        query: gql`
          query GetBookViewerURL($id: ID!) {
            url: getBookViewerUrl(id: $id)
          }
        `,
        variables: {
          id,
        },
      });
      const data: any = res.data;
      url = data.url;
    } catch (error) {
      console.error(error);
      return;
    }

    if (BUILD_TARGET === "BOOK_VIEWER") {
      const decURL = getQueryVariablesFromURL(url);
      const vars: any = {
        ...decURL.variables,
        detail_url: location.href,
        buy_url: location.href,
      };
      const newURL = `${decURL.base}${getUrlQueryFromDictionary(vars)}`;
      window.location.href = newURL;
    } else {
      window.location.href = url;
    }
  },
};
export const books = {
  namespaced: true,
  state: new State(),
  getters,
  mutations,
  actions,
};
